import React from 'react';
import { useConfirmation } from '../../hooks/ConfirmationService';
import {
  Grid,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  InputAdornment,
  Tooltip,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper
} from '@material-ui/core';
import CommentIcon from '@material-ui/icons/Comment';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { AnimalNote } from '../../models/animal-view';

interface AnimalNotesProps {
  notes: AnimalNote[];
  onNoteDelete?: (id: string) => void;
  allowAdd?: boolean;
  onNoteAdd?: (text: string) => void;
}

const AnimalViewNotes = ({ notes, onNoteDelete, allowAdd, onNoteAdd }: AnimalNotesProps) => {
  const classes = useStyles();
  const confirmDialog = useConfirmation();
  const { t } = useTranslation();

  const [noteText, setNoteText] = React.useState('');
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const openNoteDialog = () => {
    setDialogOpen(true);
  };

  const closeNoteDialog = (text: string | null) => {
    setDialogOpen(false);

    if (text != null) {
      if (onNoteAdd) {
        onNoteAdd(text);
      }
    }

    setNoteText('');
  };

  const handleDeleteNote = (note: AnimalNote) => {
    confirmDialog({
      dialogId: 'delete-note-dialog',
      title: t('f-animal-note-confirm-title'),
      message: t('f-animal-note-confirm-text', { text: note.text }),
      alertSeverity: 'warning'
    }).then(() => {
      if (onNoteDelete) {
        onNoteDelete(note.id);
      }
    });
  };

  return (
    <>
      <Grid container>
        <Grid container direction="row" alignItems="center" spacing={1}>
          <Grid item>
            <CommentIcon color="primary" fontSize="small" className={classes.notesIco} />
          </Grid>
          <Grid item>
            <Typography component="h2" variant="h6" color="primary" gutterBottom className={classes.notesTitle}>
              {t('f-animal-note')}
            </Typography>
          </Grid>
          {allowAdd === true && (
            <Grid item className={classes.addNoteGrid}>
              <Tooltip arrow title={t('f-animal-note-add-arrow')}>
                <IconButton
                  id="addNoteBtn"
                  aria-label="add-note"
                  color="primary"
                  className={classes.addNoteButton}
                  onClick={() => {
                    openNoteDialog();
                  }}
                >
                  <Icon>{'add_circle_outline'}</Icon>
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <Table size="small">
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell>{t('f-animal-note-text')}</TableCell>
                  <TableCell>{t('f-animal-note-created')}</TableCell>
                  <TableCell align="right">{t('f-com-action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notes && notes.length > 0 ? (
                  notes.map((note) => (
                    <TableRow key={note.id}>
                      <TableCell component="th" scope="row" className={classes.noteTextCell}>
                        {note.text}
                      </TableCell>
                      <TableCell>{new Date(note.created).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Tooltip arrow title={t('f-animal-note-delete')}>
                          <IconButton
                            aria-label="delete"
                            color="primary"
                            size="small"
                            onClick={() => {
                              handleDeleteNote(note);
                            }}
                          >
                            <Icon>{'delete_outline'}</Icon>
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {t('f-animal-note-empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      {/* add new animal note dialog */}
      <Dialog open={dialogOpen} onClose={() => closeNoteDialog(null)} aria-labelledby="form-dialog-title">
        <DialogTitle id="add-note-dialog" className={classes.dialogHeaderFooter}>
          {t('f-animal-note-add-title')}
        </DialogTitle>

        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="note-text"
            label="Text"
            type="text"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            fullWidth
            multiline
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CommentIcon color="primary" />
                </InputAdornment>
              )
            }}
          />
        </DialogContent>

        <DialogActions className={classes.dialogHeaderFooter}>
          <Button
            variant="outlined"
            startIcon={<Icon>{'close'}</Icon>}
            onClick={() => closeNoteDialog(null)}
            color="primary"
          >
            {t('f-com-cancel')}
          </Button>
          <Button
            onClick={() => closeNoteDialog(noteText)}
            startIcon={<Icon>{'save'}</Icon>}
            id="save-note-btn"
            variant="contained"
            color="primary"
          >
            {t('f-com-save')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* add new animal note dialog */}
    </>
  );
};

export default AnimalViewNotes;
